import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import {
  selectLatestDatasetReleaseDisplayName,
  selectLatestDatasetReleaseId,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { ConceptGroupDetailsDialog } from '@verantos-dev/feature-concept-groups-list';
import { Icon, Tooltip } from '@verantos-dev/shared-components';

import type { LogicNode } from '../../utils/analyticalVariable.utils';
import {
  deriveCategoryFromAnalyticalVariable,
  formatSelfJoinChipLabel,
  getNodeLabel,
  isDomainFilter,
  isFilterGroup,
  isTemporalGroup,
} from '../../utils/analyticalVariable.utils';
import { useAnalyticalVariableCardContext } from '../AnalyticalVariableCard/AnalyticalVariableCardContext';
import {
  getLeftBorderColor,
  getLeftBorderWidth,
  sectionLabelSx,
} from '../analyticalVariableCard.constants';
import { CollapsibleLogicSection } from './CollapsibleLogicSection';
import { LogicNodeRenderer } from './LogicNodeRenderer';
import { useAVLogicCard } from './useAVLogicCard';
import { useConceptGroupChip } from './useConceptGroupChip';

interface LogicTreeProps {
  // Nullable: SQL-only AVs carry logic: null (OR-6069); the `!logicNode`
  // guard below treats null and undefined the same — the "no logic" message.
  logicNode: LogicNode | null | undefined;
  onChipClick: (name: string) => void;
  isEditable: boolean;
}

const LogicTree = ({ logicNode, onChipClick, isEditable }: LogicTreeProps) => {
  const intl = useIntl();

  if (!logicNode) {
    return (
      <Typography variant="body2" color="text.secondary">
        {intl.formatMessage({ id: 'projects.analyticalVariables.detailModal.noLogic' })}
      </Typography>
    );
  }

  if (isFilterGroup(logicNode)) {
    return <LogicNodeRenderer node={logicNode} onChipClick={onChipClick} />;
  }

  // Single-component logic: always show detail, no collapse toggle
  if (isDomainFilter(logicNode)) {
    return (
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderLeft: getLeftBorderWidth(0),
          borderLeftColor: getLeftBorderColor(0),
          borderRadius: 2,
          p: 2,
        }}
      >
        <LogicNodeRenderer node={logicNode} onChipClick={onChipClick} />
      </Box>
    );
  }

  const chipLabel =
    (isTemporalGroup(logicNode) && formatSelfJoinChipLabel(logicNode)) ||
    getNodeLabel(intl, logicNode);

  return (
    <CollapsibleLogicSection label={chipLabel} defaultOpen={isEditable}>
      <LogicNodeRenderer node={logicNode} onChipClick={onChipClick} />
    </CollapsibleLogicSection>
  );
};

export const AVLogicCard = () => {
  const { analyticalVariable, conceptGroups, isEditable } = useAnalyticalVariableCardContext();
  const {
    intl,
    onDrillDownClick,
    logicNode,
    logicHeaderName,
    isDrillDownDisabled,
    drillDownTooltip,
    isValidating,
  } = useAVLogicCard(analyticalVariable);
  const { selectedChipConceptGroupId, handleChipClick, handleCloseConceptGroupDetails } =
    useConceptGroupChip(conceptGroups);
  const datasetReleaseId = useCohortDefinitionStore(selectLatestDatasetReleaseId);
  const datasetReleaseDisplayName = useCohortDefinitionStore(selectLatestDatasetReleaseDisplayName);

  const category = deriveCategoryFromAnalyticalVariable(analyticalVariable);

  return (
    <Box data-testid="av-detail-logic" sx={{ mb: 2 }}>
      <Box
        data-testid="av-detail-logic-header"
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap={1}
        justifyContent="space-between"
      >
        <Typography
          data-testid="av-detail-logic-label"
          variant="body1"
          sx={{ ...sectionLabelSx, fontWeight: 'regular' }}
        >
          {intl.formatMessage(
            { id: 'projects.analyticalVariables.detailModal.logicDynamic' },
            {
              name: (
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {logicHeaderName}
                </Box>
              ),
            }
          )}
        </Typography>
        {category === 'clinical' && (
          <Tooltip
            id={`av-logic-drill-down-tooltip-${analyticalVariable.id}`}
            title={drillDownTooltip ?? ''}
            placement="top"
          >
            <span>
              <IconButton
                size="small"
                onClick={onDrillDownClick}
                disabled={isDrillDownDisabled}
                data-testid={`av-logic-drill-down-${analyticalVariable.id}`}
              >
                {isValidating ? (
                  <CircularProgress
                    size={20}
                    data-testid={`av-logic-drill-down-loading-${analyticalVariable.id}`}
                  />
                ) : (
                  <Icon
                    id={`av-logic-drilldown-${analyticalVariable.id}`}
                    icon="insert_chart"
                    height={20}
                    width={20}
                  />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <Box data-testid="av-detail-logic-tree" sx={{ mt: 0.5 }}>
        <LogicTree logicNode={logicNode} onChipClick={handleChipClick} isEditable={isEditable} />
      </Box>

      {selectedChipConceptGroupId && (
        <ConceptGroupDetailsDialog
          conceptGroupId={selectedChipConceptGroupId}
          onClose={handleCloseConceptGroupDetails}
          datasetReleaseId={datasetReleaseId}
          datasetReleaseDisplayName={datasetReleaseDisplayName}
        />
      )}
    </Box>
  );
};
